// @givmo/social — the module's slug, its strings and every address beneath its mount (SMM-WEB1;
// Social Media Manager Module plan §SMM-8 point 1).
//
// ONE package, mounted at `/social` in both class consoles with no switch (D-SMM-9, D-SMM-14): the
// square and the pages show wherever the backend's verdict (offered + on + capability) says, so an
// organization not offered Social sees nothing. The package names no class and no route literal of
// its host: a host registers these paths and hands the package its organization and permissions.

/** The catalog slug: the key the console's standing read reports Social under. */
export const SOCIAL_SLUG = 'social';

/** The mount. The registry row's `path` in the company console and the class-table row's in the
 *  charity console. */
export const SOCIAL_ROUTE = '/social';

/** Members' read string: the post history and the organization's connected accounts. */
export const SOCIAL_READ_PERMISSION = 'social:read';
/** Members' write string: write, schedule, publish, cancel and retry a post. */
export const SOCIAL_PUBLISH_PERMISSION = 'social:publish';
/** Owners' custody string: connect, choose, reconnect and disconnect accounts (D-SMM-1). */
export const SOCIAL_MANAGE_CONNECTIONS_PERMISSION = 'social:manage_connections';

/** The store's purpose for Social's accounts (backend `org_integrations.py`, SMM-BE8). */
export const SOCIAL_PURPOSE = 'social_publishing';

/** The root testid every Social page carries. */
export const SOCIAL_PAGE_TEST_ID = 'page-social';

/**
 * Every Social address beneath the mount. Each first segment is static (`posts`, `new`,
 * `accounts`, `connected`), so the shell's own `/social/team` and `/social/sign-in` keep their
 * answers.
 *
 * ⚠ `connected` IS REGISTERED WITH THE PLATFORMS. The OAuth return page's address is each
 * console's redirect URI (`<origin>/social/connected`), listed in the backend's
 * `ORG_INTEGRATIONS_OAUTH_REDIRECT_URIS` and in Meta's and LinkedIn's app dashboards, which
 * match it exactly. Moving it is an operator change in every environment, never a refactor.
 */
export const SOCIAL_ROUTES = {
  posts: SOCIAL_ROUTE,
  compose: `${SOCIAL_ROUTE}/new`,
  post: `${SOCIAL_ROUTE}/posts/$postId`,
  accounts: `${SOCIAL_ROUTE}/accounts`,
  connected: `${SOCIAL_ROUTE}/connected`,
} as const;

/** Which Social page an address shows: the host route names it, with its params. */
export type SocialView =
  | { page: 'posts' }
  | { page: 'compose' }
  | { page: 'post'; postId: string }
  | { page: 'accounts' }
  | { page: 'connected' };

/** The address of a page beneath the mount, its params encoded. */
export function socialPath(view: SocialView): string {
  switch (view.page) {
    case 'posts':
      return SOCIAL_ROUTES.posts;
    case 'compose':
      return SOCIAL_ROUTES.compose;
    case 'post':
      return `${SOCIAL_ROUTE}/posts/${encodeURIComponent(view.postId)}`;
    case 'accounts':
      return SOCIAL_ROUTES.accounts;
    case 'connected':
      return SOCIAL_ROUTES.connected;
  }
}

/**
 * The redirect URI for this console: its running origin and the return page. Read from
 * `location.origin` at the click, never a build constant, so it cannot disagree with the console
 * the person is on.
 */
export function socialRedirectUri(origin: string = globalThis.location?.origin ?? ''): string {
  return `${origin}${SOCIAL_ROUTES.connected}`;
}
